import React, { useState, useContext, useEffect } from "react";
import styled from "@emotion/styled";
import cookies from "cookies-js";
import { cookiesWork } from "@src/utils/cookies";
import { useLocation } from "@reach/router";
import { AppDataContext } from "@src/components/AppDataContext";
import { LocalStorageWrapper } from "@src/utils/localStorage";
import window from "global/window";
import Helmet from "react-helmet";
import { GTagScript, MetaScript, PinterestScript, TikTokScript } from "@src/utils/TrackingScripts";

const urlsWithExternalTracking = [
  "/",
  "/schools",
  "/teachers",
  "/schoolleaders",
  "/resources",
  "/privacy",
  "/terms",
  "/about",
  "/points",
  "/training",
  "/privacycenter",
  "/activity-corner",
  "/schoolleader",
  "/p/bts-2023-class-story",
  "/p/bts-2023-messaging",
  "/p/bts-2023-points",
  "/p/bts-2023-quiet-hours",
];

const shouldFireScriptTag = (pathname: string) => {
  return urlsWithExternalTracking.some((url) => pathname.endsWith(url));
};

type TrackingPixelsProps = {
  targetingCookieConsent: boolean;
};
const TrackingPixels: React.FC<TrackingPixelsProps> = ({ targetingCookieConsent }) => {
  const appData = useContext(AppDataContext);

  const searchParams = useLocation().search;
  const referrer = window.document ? window.document.referrer : null;
  const sessionIdCookie = cookiesWork() ? cookies.get("dojo_log_session_id") : null;
  const paramsEntries = new URLSearchParams(searchParams);
  const paramsAsObject = Object.fromEntries(paramsEntries);
  const initReferrerObjectInLocalStorage = LocalStorageWrapper.getItem("init-referrer-data");
  const lastReferrerObjectInLocalStorage = LocalStorageWrapper.getItem("last-referrer-data");

  function writeReferralData(localStorageKey: string) {
    const referrerObject = { sessionIdCookie: sessionIdCookie, ...paramsAsObject, referrerUrl: referrer };
    const referrerObjectAsString = JSON.stringify(referrerObject);
    LocalStorageWrapper.setItem(localStorageKey, referrerObjectAsString);
  }

  useEffect(() => {
    if (sessionIdCookie) {
      if (!initReferrerObjectInLocalStorage) {
        writeReferralData("init-referrer-data");
      }

      if (
        initReferrerObjectInLocalStorage &&
        sessionIdCookie !== JSON.parse(initReferrerObjectInLocalStorage).sessionIdCookie
      ) {
        writeReferralData("last-referrer-data");
      } else if (
        lastReferrerObjectInLocalStorage &&
        sessionIdCookie !== JSON.parse(lastReferrerObjectInLocalStorage).sessionIdCookie
      ) {
        LocalStorageWrapper.removeItem("last-referrer-data");
        writeReferralData("last-referrer-data");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionIdCookie]);

  useEffect(() => {
    if (!cookiesWork()) return;
    if (!targetingCookieConsent) return;

    const params = new URLSearchParams(searchParams);
    const campaign = params.get("utm_campaign");
    const source = params.get("utm_source");
    const medium = params.get("utm_medium");

    if (!cookies.get("attribution") && campaign && source) {
      cookies.set("attribution", source);
    }

    if (medium === "cpc" && campaign && source) {
      cookies.set("paid_attribution", `campaign=${campaign}&source=${source}`, { expires: 2592000 });
    }
  }, [targetingCookieConsent]);

  useEffect(() => {
    const isTeacher = appData.data.type === "teacher";
    if (isTeacher && targetingCookieConsent) {
      // window.gtag?.("event", "conversion", { send_to: "AW-11184552360/-u7ICLiqt6wYEKjzmtUp" });
      window.fbq?.("track", "Lead");
      window.pintrk?.("track", "lead");
      window.ttq?.track?.("Contact", {
        contents: [
          {
            content_id: `${window.location}`,
          },
        ],
      });
    }
  }, [targetingCookieConsent, appData, window.gtag, window.fbq, window.pintrk]);

  return (
    <>
      {targetingCookieConsent && shouldFireScriptTag && (
        <Helmet>
          {/* <script
            async
            className="optanon-category-C0004"
            src="https://www.googletagmanager.com/gtag/js?id=AW-11184552360"
          ></script>
          <script async defer className="optanon-category-C0004">{GTagScript}</script> */}
          <script async defer className="optanon-category-C0004">
            {MetaScript}
          </script>
          <script async defer className="optanon-category-C0004">
            {PinterestScript}
          </script>
          <script async defer className="optanon-category-C0004">
            {TikTokScript}
          </script>
        </Helmet>
      )}
    </>
  );
};

export default TrackingPixels;
